/**
 * Component attributes - types, families, density level, etc
 * @module ComponentAttribute
 */
module.exports = {
    /**
     * @enum {string}
     */
    type: {
        SOIC_SOP: 'SOIC, SOP',
        CFP: 'CFP',
        QFP: 'QFP',
        CQFP: 'CQFP',
        QFN: 'QFN',
        SOT23: 'SOT23',
        SOT223: 'SOT223',
        SOT143: 'SOT143',
        SOTFL: 'SOTFL',
        CHIP: 'CHIP',
        CHIP_POLARIZED: 'CHIP Polarized', //used for migration
        CHIP_NON_POLARIZED: 'CHIP Non-Polarized', //used for migration
        SOD: 'SOD',
        SODFL: 'SODFL',
        MOLDED_BODY: 'Molded Body',
        MOLDED_BODY_POLARIZED: 'Molded Body Polarized', //used for migration
        MOLDED_BODY_NON_POLARIZED: 'Molded Body Non-Polarized', //used for migration
        TO_DPAK: 'TO, DPAK',
        DFN_2: 'DFN-2',
        DFN_3: 'DFN-3',
        DFN_4: 'DFN-4',
        ALUMINUM_ELECTROLYTIC_CAPACITOR: 'Aluminum Electrolytic Capacitor',
        ALUMINUM_ELECTROLYTIC: 'Aluminum Electrolytic', //used for migration
        CRYSTAL: 'Crystal',
        BGA: 'BGA',
        MELF: 'Metal Electrode Leadless Face (MELF)',
        CHIP_ARRAY_TWO_SIDE_FLAT_CONCAVE: 'Chip Array, 2 Side Flat/Concave',
        CHIP_ARRAY_TWO_SIDE_CONVEX: 'Chip Array, 2 Side Convex',
        CHIP_ARRAY_FOUR_SIDE_FLAT_CONCAVE: 'Chip Array, 4 Side Flat/Concave',
        PLCC: 'Plastic Leaded Chip Carrier (PLCC)',
        OSCILLATOR_CORNER_CONCAVE: 'Oscillator, Corner Concave',
        OSCILLATOR_J_LEAD: 'Oscillator, J-Lead',
        OSCILLATOR_L_BEND: 'Oscillator, L-Bend',
        SOJ: 'Small Outline J-Lead (SOJ)',
    },

    /**
     * @enum {string}
     */
    chipComponentFamily: {
        "RESISTOR": 'Resistor',
        "CAPACITOR": 'Capacitor - Non polarised',
        "FERRITE_BEAD": 'Ferrite Bead',
        "FUSE": 'Fuse',
        "INDUCTOR": 'Inductor',
        "THERMISTOR": 'Thermistor',
        "VARISTOR": 'Varistor',
        "POLARIZED_CAPACITOR": 'Polarized Capacitor',
        "POLARIZED_DIODE": 'Diode',
        //"LED": 'LED'
    },

    /**
     * @enum {string}
     */
    chipArrayComponentFamily: {
        FLAT_CONCAVE: {//2 or 4 side
            "RESISTOR": 'Resistor',
            "INDUCTOR": 'Inductor',
            "DIODE": 'Diode',
            "CAPACITOR_NONPOLARIZED": 'Capacitor - Non polarised'
        },

        TWO_SIDE_CONVEX: {
            "RESISTOR": 'Resistor',
            "INDUCTOR": 'Inductor',
            "CAPACITOR_NONPOLARIZED": 'Capacitor - Non polarised'
        }
    },

    /**
     * @enum {string}
     */
    moldedBodyComponentFamily: {
        "RESISTOR": 'Resistor',
        "CAPACITOR": 'Capacitor - Non polarised',
        "FUSE": 'Fuse',
        "INDUCTOR": 'Inductor',
        "PRECISION_INDUCTOR": 'Precision Inductor',
        "POLARIZED_CAPACITOR": 'Polarized Capacitor',
        "POLARIZED_DIODE": 'Diode',
        "LED": 'LED'
    },

    /**
     * @enum {string}
     */
    dfnComponentFamily: {
        DFN_2: {
            "RESISTOR": 'Resistor',
            "CAPACITOR_NON_POLARIZED": 'Capacitor - Non polarised',
            "DIODE": 'Diode',
            "INDUCTOR": 'Inductor',
            "FUSE": 'Fuse',
            "CRYSTAL": 'Crystal'
        },

        DFN_34: {
            "RESISTOR": 'Resistor',
            "CAPACITOR_NON_POLARIZED": 'Capacitor - Non polarised',
            "DIODE": 'Diode',
            "INDUCTOR": 'Inductor',
            "FILTER": 'Filter',
            "IC": 'Integrated Circuit (IC)',
            "TRANSISTOR": 'Transistor'
        }
    },

    /**
     * @enum {string}
     */
    melfComponentFamily: {
        "RESISTOR": 'Resistor',
        "DIODE": 'Diode'
    },

    /**
     * Terminal type of BGA package
     * Types: collapsing and non-collapsing ball
     * @enum {string}
     */
    bgaTerminalType: {
        "Collapsing_Ball": 'Collapsing Ball',
        "Non_Collapsing_Ball": 'Non Collapsing Ball'
    },

    /**
     * Lead shape of chip array
     * @enum {string}
     */
    chipArrayLeadShapeType: {
        "CONCAVE": 'Concave',
        "FLAT": 'Flat'
    },

    chipLedLensType: {
        "RECT_FLAT_TOP": 'Rectangle with Flat Top',
        "RECT_DOMED_TOP": 'Rectangle with Domed Top'
    },

    /**
     * @enum {number}
     */
    densityLevel: {
        'Most (M)': 0,
        'Nominal (N)': 1,
        'Least (L)': 2
    },

    /*
        @enum {string}
     */
    padShapes: {
        "Rectangle": 'Rectangle',
        "RoundedRectangle": 'Rounded Rectangle',
        "Oblong": 'Oblong'
    },

    /**
     * Optional input of Ecap and crystal
     *   @enum {string}
     */
    optionalDimensionInput: {
        'Optional_D': 'D (Lead Span)',
        'Optional_D2': 'D2 (Terminal Gap)',
        'Optional_L': 'L (Terminal Length)'
    },

    optionalCustomDimensionInput: {
        'Optional_g': 'g (Custom Pad Gap)',
        'Optional_p': 'p (Custom Pad Pitch)'
    },

    bodyColors: {
        CHIP:{
            LED:{
                Green: {key:"Green", value:'#1E5F41'},
                Blue: {key:"Blue", value:'#1825F8'},
                Yellow: {key:"Yellow", value:'#FFFF00'},
                White: {key:"White", value:'#FFFFFF'},
                Red: {key:"Red", value:'#FF0000'},
                Amber: {key:"Amber", value:'#FA9641'},
                Purple: {key:"Purple", value:'#800080'},
                Black: {key:"Black", value:'#0A0A0A'},
                Custom: {key:"Custom", value: ''}

            }

        }
    }
}
